import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ApiService } from './api.service';
import { ErrorHandlingService } from './error-handling.service';
import { NotificationService } from './notification.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class Authentication {
  refreshToken: string;
  accessToken = new BehaviorSubject<string>(null);

  constructor(
    private _api: ApiService,
    private jwtHelper: JwtHelperService,
    private ngxLoader: NgxUiLoaderService,
    private router: Router,
    private errorHandlingService: ErrorHandlingService,
    private notificationService: NotificationService,
    private userService: UserService,
  ) {}

  login(username: string, password: string): Observable<any> {
    let res = this._api.postWithoutAuth(`/auth/token`, { username, password, grant_type: 'password' }).pipe(map((data) => data));
    return res;
  }

  successAuthentication(response: any): void {
    const refreshToken = response.refresh_token;
    const accessToken = response.access_token;
    this.refreshToken = refreshToken;
    this.accessToken.next(accessToken);
    localStorage.setItem('access_token_automap', accessToken);
    localStorage.setItem('refresh_token_automap', refreshToken);
    this.userService.getAllUsers().subscribe((data) => {
      localStorage.setItem('user_data_automap', JSON.stringify(data.items[0]));
      this.userService.getUserDetails();
    });
    this.userService.flush();
    this.navigateToReturnUrl();
    this.ngxLoader.stop();
    this.notificationService.showSuccess('Logged in Successfully');
  }

  errorAuthentication(error: any) {
    this.ngxLoader.stop();
    this.errorHandlingService.handleError(error, error.url);
    if (error.status === 417) {
      this.errorHandlingService.showError(error.message);
      return error;
    }
    if (error.status === 401) {
      this.errorHandlingService.showError(error.message);
      return error;
    }
  }

  whitelistedUrls(): boolean {
    return (
      this.router.url.includes('/login') ||
      this.router.url.includes('/signup') ||
      this.router.url.includes('/email-verify') ||
      this.router.url.includes('/password-reset')
    );
  }

  navigateToReturnUrl(): void {
    if (localStorage.getItem('returnUrl')) {
      this.router.navigateByUrl(localStorage.getItem('returnUrl'));
      localStorage.removeItem('returnUrl');
    } else {
      this.router.navigateByUrl(localStorage.getItem('/automap'));
    }
  }

  refreshTokenAndRetry(): Observable<any> {
    if (!localStorage.getItem('refresh_token_automap')) {
      if (!this.router.url.includes('/login') && !this.router.url.includes('/signup') && !this.router.url.includes('/password-reset')) {
        this.errorHandlingService.notAuthenticated();
      }
    }
    const body = {
      grant_type: 'refresh_token',
      refresh_token: localStorage.getItem('refresh_token_automap'),
    };
    return this._api.postWithoutAuth<any>(`/auth/token`, body).pipe(
      tap((response) => {
        const newAccessToken = response.access_token;
        const newRefreshToken = response.refresh_token;
        localStorage.setItem('access_token_automap', newAccessToken);
        localStorage.setItem('refresh_token_automap', newRefreshToken);
        this.accessToken.next(newAccessToken);
      }),
      catchError((error) => {
        return this.errorHandlingService.handleError(error, error.url);
      }),
    );
  }

  getAccessToken(): string {
    return localStorage.getItem('access_token_automap');
  }

  isTokenValid(manualToken?: string): boolean {
    const token = !manualToken ? this.getAccessToken() : manualToken;
    if (token) {
      // valid if not expired
      return !this.jwtHelper.isTokenExpired(token);
    }
    return false;
  }

  isAccountToken(manualToken?: string): boolean {
    const token = !manualToken ? this.getAccessToken() : manualToken;

    if (token) {
      // Decode the token to get claims
      const claims = this.jwtHelper.decodeToken(token);
      // valid if not an ACCOUNT token
      return claims['termhub:role'] !== 'ACCOUNT';
    }
    return false;
  }

  logout(): void {
    this.accessToken.next(null);
    localStorage.removeItem('access_token_automap');
    localStorage.removeItem('user_data_automap');
    localStorage.removeItem('refresh_token_automap');
    this.refreshToken = null;
    this.router.navigateByUrl('/login');
  }

  getActiveUserDetails(): any {
    let userId = JSON.parse(localStorage.getItem('user_data_automap'));
    userId = userId['id'];
    this.ngxLoader.start();
    this._api
      .get(`/user?query=id:${userId}`)
      .pipe(map((data) => data))
      .subscribe(
        (data: any) => {
          const userData = JSON.stringify(data.items[0]);
          localStorage.setItem('user_data_automap', userData);
          this.userService.getUserDetails();
          this.ngxLoader.stop();
        },
        (err) => {
          this.ngxLoader.stop();
          this.errorHandlingService.handleError(err, err.url);
        },
      );
  }
}
